import type { CellContext, Row } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';

import type { W_JobTracking_FindTemplateTable_VM_Row } from 'ab-api/web/jobTracking/findJobTrackingTemplateTable';

import type { RootState } from 'af-reducers';

import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import Breadcrumbs from 'af-components/Breadcrumbs';
import TableNew from 'af-components/Table';
import type { TableRef } from 'af-components/Table';
import TextCell from 'af-components/Table/Cells/TextCell';
import type { TableProps } from 'af-components/Table/types';
import Tooltip from 'af-components/Tooltip';

import CLIENT from 'af-routes/client';

import * as JobTemplateActions from 'af-actions/jobTracking';

import styles from './styles.module.scss';

interface CompanySettingsRouteProps {
	orgAlias: string;
	companyName: string;
}

type OwnProps = CustomRouteComponentProps<CompanySettingsRouteProps>;

type Props = OwnProps & ConnectedProps<typeof connector>;

const JobTrackingTemplates: React.FC<Props> = (props: Props) => {

	const {
		company,
		fetchTable,
	} = props;

	const history = useHistory();
	const location = useLocation<{ orgAlias: string; }>();

	const tableRef = React.useRef<TableRef<W_JobTracking_FindTemplateTable_VM_Row>>(null);

	const buttons = React.useMemo(() => [
		{ label: 'New Template', type: TableButtonType.PRIMARY, hasPermission: true, onClick: async () => history.push(CLIENT.COMPANY.SETTINGS.JOB_TRACKING_TEMPLATES.CREATE(location.state.orgAlias, company.name)) },
	], [company?.name, history, location.state.orgAlias]);

	const fetchRows = React.useCallback(async (tableRequestModel: TableQuery): Promise<TableContent<W_JobTracking_FindTemplateTable_VM_Row>> => {
		const { page, pageSize, filterByText, sortBy } = tableRequestModel;
		const result = await fetchTable(
			new TableQuery({ page, pageSize, sortBy, filterByText })
		);
		return result;
	}, [fetchTable]);

	const renderNameCell = React.useCallback(
		() =>
			(cell: CellContext<W_JobTracking_FindTemplateTable_VM_Row, string>) => {
				const { outdatedColumnNames } = cell.row.original;
				const hasOutdated = Array.isArray(outdatedColumnNames) && outdatedColumnNames.length > 0;

				const uniqueNames = outdatedColumnNames.reduce<string[]>((acc, name) => {
					if (!name) {
						return acc;
					}
					if (!acc.includes(name)) {
						acc.push(name);
					}
					return acc;
				}, []);

				const tooltipMessage = <>
					Some changes have been made
					<br />
					to the fields used to populate the job tracking table.
					<br />
					This affects the content of certain columns.
					<br />
					Affected columns:
					<br />
					{uniqueNames.map((n) => <b key={n}>{n} <br /></b>)}
				</>;

				return (
					<div className={styles['name-cell']}>
						{hasOutdated && (
							<Tooltip message={tooltipMessage}>
								<span className={`icon-warning ${styles['warning-icon']}`} />
							</Tooltip>
						)}
						<TextCell value={cell.getValue() ?? '-'} />
					</div>
				);
			},
		[]
	);

	const renderTextCell = React.useCallback(() => (cell: CellContext<W_JobTracking_FindTemplateTable_VM_Row, string>) => {
		return <TextCell value={cell.getValue() ?? '-'} />;
	}, []);

	const renderColumnsCell = React.useCallback(() => (cell: CellContext<W_JobTracking_FindTemplateTable_VM_Row, string[]>) => {
		return <TextCell value={cell.getValue().join(', ')} />;
	}, []);

	const openTemplateEditPage = React.useCallback((row: Row<W_JobTracking_FindTemplateTable_VM_Row>) => {
		history.push(CLIENT.COMPANY.SETTINGS.JOB_TRACKING_TEMPLATES.EDIT(location.state.orgAlias, company.name, `${row.original.id}`));
	}, [history, location.state.orgAlias, company.name]);

	const columns: TableProps<W_JobTracking_FindTemplateTable_VM_Row>['columns'] = React.useMemo(() => [
		{
			id: 'name',
			accessor: 'name',
			header: 'Name',
			cell: renderNameCell(),
			enableSorting: true,
			enableHiding: true,
			size: 100,
		},
		{
			id: 'description',
			accessor: 'description',
			header: 'Description',
			cell: renderTextCell(),
			enableSorting: true,
			enableHiding: true,

		},
		{
			id: 'columns',
			accessor: 'columns',
			header: 'Columns',
			cell: renderColumnsCell(),
			enableSorting: false,
			enableHiding: true,
		},
		{
			id: 'createdAt',
			accessor: 'createdAt',
			header: 'Date Submitted',
			cell: renderTextCell(),
			enableSorting: true,
			enableHiding: true,
		},
	], [renderColumnsCell, renderNameCell, renderTextCell]);

	return (
		<div className="form-segment">
			<Breadcrumbs items={[{ label: 'Job Tracking Templates' }]} />
			<TableNew
				buttons={buttons}
				columns={columns}
				fetch={fetchRows}
				hasSearchInput={true}
				hasSubRows={false}
				offsetHeight={20}
				onRowClick={openTemplateEditPage}
				ref={tableRef}
				selectable={false}
				tableName={TableNameEnum.JOB_TRACKING_TEMPLATES}
			/>
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { user: { companyData } } = state;

	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		company: companyData,
	};
}

function mapDispatchToProps() {
	return {
		fetchTable: JobTemplateActions.findAllTemplatesForCompanyTable,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(JobTrackingTemplates);
